// Hook genérico para requests HTTP com useReducer
import { useReducer, useCallback } from 'react';
import { ApiState, ApiAction, ApiErrorInfo } from '../types/api';
import { ErrorHandler } from '../utils/errorHandler';
import { secureStorage } from '../utils/secureStorage';

// Função helper para obter token do localStorage
const getAuthToken = (): string | null => {
  return secureStorage.getItem('accessToken');
};

// Função helper para fazer logout e redirecionar
const handleAuthError = () => {
  // Limpar dados de autenticação
  secureStorage.removeItem('accessToken');
  secureStorage.removeItem('tokenExpiration');
  secureStorage.removeItem('tokenCreated');
  
  // Redirecionar para login
  window.location.href = '/login';
};

function apiReducer<T>(state: ApiState<T>, action: ApiAction<T>): ApiState<T> {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { data: action.payload, loading: false, error: null };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function useRequest<T>(initialData: T | null = null) {
  const [state, dispatch] = useReducer(apiReducer<T>, {
    data: initialData,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (url: string, options?: RequestInit) => {
    dispatch({ type: 'FETCH_START' });
    try {
      // Injetar automaticamente o token de autenticação se disponível
      const token = getAuthToken();
      const authHeaders: Record<string, string> = {};
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }

      // Mesclar headers existentes com headers de autenticação
      const mergedOptions: RequestInit = {
        ...options,
        headers: {
          ...authHeaders,
          ...options?.headers,
        },
      };

      let response;
      try {
        response = await fetch(url, mergedOptions);
      } catch (fetchError: any) {
        // Capturar erros de rede (ERR_CONNECTION_REFUSED, etc)
        console.error('Erro de conexão:', fetchError);
        
        // Se for erro de conexão e não estamos na página de login, redirecionar
        if (!window.location.pathname.includes('/login') && 
            (fetchError.message?.includes('fetch') || fetchError.name === 'TypeError')) {
          console.warn('Erro de conexão com servidor. Redirecionando para login.');
          handleAuthError();
          return;
        }
        
        throw fetchError;
      }

      if (!response.ok) {
        // Interceptar erros de autenticação e redirecionar automaticamente
        // MAS apenas se NÃO estivermos na página de login
        if ((response.status === 401 || response.status === 403) && !window.location.pathname.includes('/login')) {
          console.warn(`Token inválido detectado durante requisição (${response.status}). Redirecionando para login.`);
          handleAuthError();
          return; // Não continuar com o processamento normal
        }

        // Tenta obter o corpo do erro para processamento padronizado
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          // Se não conseguir fazer parse do JSON, cria erro genérico
          errorData = { message: `Erro ${response.status}: ${response.statusText}` };
        }

        // Cria um erro estruturado com dados da resposta
        const apiError = {
          errors: errorData,
          statusCode: response.status,
          response
        };

        throw apiError;
      }

      // Para respostas 204 No Content, não há corpo para parsear
      const data: T = response.status === 204 ? (null as T) : await response.json();

      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (error) {
      // Para erros 400 (validação), preservar as mensagens originais da API
      let errorMessage: string;
      const statusCode = (error as any)?.statusCode;

      if (statusCode === 400) {
        // Para erro 400, verificar se há estrutura de erros padronizada
        const errorData = (error as any)?.errors;
        
        // Estrutura: { statusCode, property, message }
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        } 
        // Estrutura de array: [{ statusCode, property, message }]
        else if (errorData && Array.isArray(errorData)) {
          errorMessage = errorData
            .map((err: any) => err.message)
            .join('; ');
        } 
        // Fallback genérico
        else {
          errorMessage = 'Erro de validação nos dados enviados.';
        }
      } else {
        // Para outros erros, usar o processamento padronizado
        const processedErrors = ErrorHandler.processApiError(error);
        errorMessage = processedErrors.join('; ');
      }

      // Adiciona informações adicionais sobre o tipo de erro HTTP
      const enhancedError: ApiErrorInfo = {
        message: errorMessage,
        originalError: error,
        isNotFound: ErrorHandler.isNotFoundError(error),
        isValidationError: ErrorHandler.isValidationError(error),
        isAuthError: ErrorHandler.isAuthError(error),
        statusCode: statusCode,
        httpErrorType: ErrorHandler.getHttpErrorType(statusCode || 0) || undefined
      };

      dispatch({ type: 'FETCH_ERROR', payload: enhancedError });
    }
  }, []);

  return { ...state, execute };
}